import { getVendorCategory } from './categories';
import type { CategoryCode, Receipt } from './types';

/**
 * Auto-category inference. Combines three signals:
 *
 *   1. User memory — vendor → category mapping the user established by
 *      manually picking a category for a receipt with the same vendor.
 *      Strongest signal (score 1.0); always wins over heuristic guesses.
 *   2. Vendor lookup table — hand-curated map of well-known chain
 *      substrings to categories ("starbucks" → food, "uber" → transport).
 *      Scored 0.75-0.95 depending on exact vs substring match.
 *   3. Item-keyword scoring — per-category keyword lists across the 7
 *      shipped UI languages. Item names get matched substring-style;
 *      the dominant category by match count wins, scored against the
 *      total item count.
 *
 * Returns the best guess with a 0-1 score, or null if no signal cleared
 * the apply-threshold. The store calls this only when user memory is
 * silent — auto-applies the result to `Receipt.category` and records
 * the score on `Receipt.categoryScore` so the UI can show an "auto"
 * pill that disappears when the user overrides.
 *
 * Pure / synchronous / no I/O — all data is bundled. No language
 * dependency: keywords from every supported language are tried at once,
 * which costs nothing in our 10×7 ≈ 1k-keyword scale and means a user
 * with the UI in English processing a Spanish receipt still gets the
 * Spanish item terms recognised.
 */

export interface CategoryInference {
  category: CategoryCode;
  /** 0-1; higher is more confident. */
  score: number;
  source: 'memory' | 'vendor' | 'items';
}

/** Below this we don't auto-apply; the receipt stays uncategorised. */
const APPLY_THRESHOLD = 0.5;

/**
 * Vendor substring table. Keys are lowercased, trimmed; matched as
 * substrings against the lowercased vendor name. Tier-1 chains only —
 * the goal is high precision, not recall (item keywords pick up the
 * long tail). Add to this list when a real receipt slips through.
 */
const VENDOR_TABLE: Record<string, CategoryCode> = {
  // Food — restaurants + grocery + coffee
  starbucks: 'food',
  costa: 'food',
  'pret a manger': 'food',
  pret: 'food',
  greggs: 'food',
  dunkin: 'food',
  mcdonalds: 'food',
  mcdonald: 'food',
  kfc: 'food',
  'burger king': 'food',
  subway: 'food',
  'pizza hut': 'food',
  dominos: 'food',
  'taco bell': 'food',
  chipotle: 'food',
  panera: 'food',
  wagamama: 'food',
  nandos: 'food',
  mercadona: 'food',
  carrefour: 'food',
  lidl: 'food',
  aldi: 'food',
  tesco: 'food',
  sainsbury: 'food',
  asda: 'food',
  waitrose: 'food',
  morrisons: 'food',
  walmart: 'food',
  kroger: 'food',
  'whole foods': 'food',
  'trader joe': 'food',
  safeway: 'food',
  publix: 'food',
  costco: 'food',
  monoprix: 'food',
  auchan: 'food',
  intermarche: 'food',
  rewe: 'food',
  edeka: 'food',
  kaufland: 'food',
  conad: 'food',
  esselunga: 'food',
  // Transport — rideshare + rail + airline + fuel
  uber: 'transport',
  lyft: 'transport',
  bolt: 'transport',
  cabify: 'transport',
  'free now': 'transport',
  sncf: 'transport',
  ouigo: 'transport',
  'deutsche bahn': 'transport',
  ratp: 'transport',
  tfl: 'transport',
  ryanair: 'transport',
  easyjet: 'transport',
  vueling: 'transport',
  lufthansa: 'transport',
  iberia: 'transport',
  shell: 'transport',
  'bp ': 'transport',
  total: 'transport',
  totalenergies: 'transport',
  repsol: 'transport',
  cepsa: 'transport',
  esso: 'transport',
  exxon: 'transport',
  chevron: 'transport',
  texaco: 'transport',
  // Healthcare — pharmacy / drugstore
  cvs: 'healthcare',
  walgreens: 'healthcare',
  boots: 'healthcare',
  'rite aid': 'healthcare',
  // Travel — hotel / booking
  marriott: 'travel',
  hilton: 'travel',
  hyatt: 'travel',
  'holiday inn': 'travel',
  ibis: 'travel',
  novotel: 'travel',
  airbnb: 'travel',
  'booking.com': 'travel',
  expedia: 'travel',
  trivago: 'travel',
  // Utilities — telecom / energy
  verizon: 'utilities',
  't-mobile': 'utilities',
  vodafone: 'utilities',
  orange: 'utilities',
  comcast: 'utilities',
  xfinity: 'utilities',
  'british gas': 'utilities',
  edf: 'utilities',
  'e.on': 'utilities',
  movistar: 'utilities',
  endesa: 'utilities',
  // Subscriptions — SaaS / streaming
  netflix: 'subscriptions',
  spotify: 'subscriptions',
  hulu: 'subscriptions',
  'disney+': 'subscriptions',
  'apple.com/bill': 'subscriptions',
  github: 'subscriptions',
  'aws ': 'subscriptions',
  'amazon web services': 'subscriptions',
  dropbox: 'subscriptions',
  notion: 'subscriptions',
  slack: 'subscriptions',
  zoom: 'subscriptions',
  adobe: 'subscriptions',
  // Entertainment — cinema / events
  amc: 'entertainment',
  cinemark: 'entertainment',
  regal: 'entertainment',
  cineworld: 'entertainment',
  vue: 'entertainment',
  ticketmaster: 'entertainment',
  eventbrite: 'entertainment',
  // Office — supplies
  staples: 'office',
  'office depot': 'office',
  ryman: 'office',
  // Personal — beauty / grooming
  ulta: 'personal',
  sephora: 'personal',
  supercuts: 'personal',
};

/**
 * Item-keyword table. Per-category lists, tokens lowercased. Matched
 * substring-style against item names. Multi-language: every supported
 * UI language contributes to the same flat list per category — a
 * Spanish receipt with `leche` items still scores `food` even when
 * the UI is in English.
 */
const ITEM_KEYWORDS: Record<CategoryCode, readonly string[]> = {
  food: [
    'restaurant',
    'cafe',
    'coffee',
    'espresso',
    'cappuccino',
    'americano',
    'mocha',
    'macchiato',
    'croissant',
    'bagel',
    'donut',
    'doughnut',
    'muffin',
    'scone',
    'pizza',
    'burger',
    'sandwich',
    'milk',
    'bread',
    'eggs',
    'butter',
    'cheese',
    'yogurt',
    'fruit',
    'vegetable',
    'water',
    'juice',
    'beer',
    'wine',
    'dinner',
    'lunch',
    'breakfast',
    'meal',
    'food',
    'grocery',
    'bakery',
    'deli',
    'restaurante',
    'cafetería',
    'café',
    'comida',
    'leche',
    'pan',
    'queso',
    'cerveza',
    'vino',
    'panadería',
    'repas',
    'dîner',
    'déjeuner',
    'lait',
    'pain',
    'fromage',
    'bière',
    'vin',
    'boulangerie',
    'kaffee',
    'essen',
    'brot',
    'milch',
    'käse',
    'bier',
    'wein',
    'bäckerei',
    'eten',
    'brood',
    'melk',
    'kaas',
    'bakkerij',
    'ristorante',
    'caffè',
    'pasto',
    'cena',
    'pranzo',
    'latte',
    'panetteria',
    'jantar',
    'almoço',
    'leite',
    'pão',
    'queijo',
    'cerveja',
    'vinho',
    'padaria',
  ],
  transport: [
    'uber',
    'lyft',
    'taxi',
    'train',
    'bus',
    'subway',
    'metro',
    'fuel',
    'gas',
    'diesel',
    'petrol',
    'gasoline',
    'fare',
    'autobús',
    'gasolina',
    'gasoil',
    'billete',
    'boleto',
    'métro',
    'autobus',
    'essence',
    'ticket',
    'billet',
    'zug',
    'benzin',
    'fahrkarte',
    'trein',
    'benzine',
    'kaartje',
    'treno',
    'biglietto',
    'táxi',
    'comboio',
    'autocarro',
    'gasóleo',
    'bilhete',
  ],
  office: [
    'pen',
    'pencil',
    'paper',
    'notebook',
    'printer',
    'ink',
    'toner',
    'stapler',
    'folder',
    'envelope',
    'office',
    'stationery',
    'bolígrafo',
    'lápiz',
    'papel',
    'cuaderno',
    'impresora',
    'oficina',
    'stylo',
    'crayon',
    'papier',
    'cahier',
    'imprimante',
    'bureau',
    'fournitures',
    'stift',
    'bleistift',
    'heft',
    'drucker',
    'büro',
    'potlood',
    'schrift',
    'kantoor',
    'penna',
    'matita',
    'carta',
    'quaderno',
    'stampante',
    'ufficio',
    'cartoleria',
    'caneta',
    'lápis',
    'caderno',
    'impressora',
    'escritório',
    'papelaria',
  ],
  healthcare: [
    'pharmacy',
    'drug',
    'drugstore',
    'prescription',
    'medicine',
    'medication',
    'doctor',
    'clinic',
    'hospital',
    'dental',
    'dentist',
    'vitamin',
    'ibuprofen',
    'paracetamol',
    'aspirin',
    'bandage',
    'farmacia',
    'medicamento',
    'receta',
    'médico',
    'clínica',
    'dentista',
    'vitamina',
    'ibuprofeno',
    'pharmacie',
    'médicament',
    'ordonnance',
    'médecin',
    'clinique',
    'hôpital',
    'dentiste',
    'vitamine',
    'ibuprofène',
    'paracétamol',
    'apotheke',
    'medikament',
    'rezept',
    'arzt',
    'klinik',
    'krankenhaus',
    'zahnarzt',
    'apotheek',
    'medicijn',
    'recept',
    'arts',
    'kliniek',
    'ziekenhuis',
    'tandarts',
    'farmaco',
    'ricetta',
    'medico',
    'clinica',
    'ospedale',
    'farmácia',
    'receita',
  ],
  travel: [
    'hotel',
    'motel',
    'flight',
    'airline',
    'airport',
    'rail',
    'booking',
    'reservation',
    'hostel',
    'airbnb',
    'ferry',
    'cruise',
    'lodging',
    'vuelo',
    'aerolínea',
    'aeropuerto',
    'reserva',
    'vol',
    'aéroport',
    'réservation',
    'auberge',
    'flug',
    'fluggesellschaft',
    'flughafen',
    'reservierung',
    'vlucht',
    'luchthaven',
    'reservering',
    'volo',
    'aeroporto',
    'prenotazione',
    'ostello',
    'voo',
    'aeroporto',
  ],
  utilities: [
    'electric',
    'electricity',
    'water',
    'internet',
    'broadband',
    'phone',
    'mobile',
    'cable',
    'sewer',
    'trash',
    'utility',
    'electricidad',
    'agua',
    'teléfono',
    'móvil',
    'factura',
    'électricité',
    'eau',
    'gaz',
    'téléphone',
    'portable',
    'facture',
    'strom',
    'wasser',
    'telefon',
    'rechnung',
    'kabel',
    'elektriciteit',
    'telefoon',
    'mobiel',
    'elettricità',
    'fattura',
    'eletricidade',
    'água',
    'telefone',
    'telemóvel',
    'fatura',
  ],
  entertainment: [
    'cinema',
    'movie',
    'theater',
    'theatre',
    'concert',
    'museum',
    'gallery',
    'gym',
    'club',
    'bar',
    'pub',
    'cine',
    'película',
    'teatro',
    'concierto',
    'entrada',
    'museo',
    'galería',
    'gimnasio',
    'cinéma',
    'film',
    'théâtre',
    'musée',
    'galerie',
    'kino',
    'konzert',
    'fitnessstudio',
    'bioscoop',
    'sportschool',
    'palestra',
    'filme',
    'concerto',
    'ginásio',
  ],
  subscriptions: [
    'subscription',
    'monthly',
    'annual',
    'membership',
    'recurring',
    'plan',
    'suscripción',
    'mensual',
    'anual',
    'membresía',
    'abonnement',
    'mensuel',
    'annuel',
    'adhésion',
    'abo',
    'monatlich',
    'jährlich',
    'mitgliedschaft',
    'maandelijks',
    'jaarlijks',
    'lidmaatschap',
    'abbonamento',
    'mensile',
    'annuale',
    'iscrizione',
    'assinatura',
    'mensal',
  ],
  personal: [
    'salon',
    'haircut',
    'barber',
    'spa',
    'massage',
    'manicure',
    'pedicure',
    'nail',
    'beauty',
    'skincare',
    'makeup',
    'peluquería',
    'barbero',
    'masaje',
    'manicura',
    'pedicura',
    'belleza',
    'coupe',
    'coiffeur',
    'barbier',
    'manucure',
    'beauté',
    'friseur',
    'maniküre',
    'schönheit',
    'kosmetik',
    'kapsalon',
    'kapper',
    'schoonheid',
    'parrucchiere',
    'barbiere',
    'massaggio',
    'bellezza',
    'cabeleireiro',
    'barbeiro',
    'massagem',
    'beleza',
  ],
  // Pure fallback — no keywords. The dropdown still has it if the user
  // wants to mark a receipt explicitly "Other".
  other: [],
};

/**
 * Single-vendor lookup. Returns the matching code + score, where exact
 * matches outscore substring matches so a multi-keyword vendor like
 * `"shell"` doesn't lose to a longer false-positive substring elsewhere.
 */
function matchVendorTable(vendor: string): { code: CategoryCode; score: number } | null {
  const v = vendor.trim().toLowerCase();
  if (v === '') return null;
  // Exact match wins.
  const exact = VENDOR_TABLE[v];
  if (exact !== undefined) return { code: exact, score: 0.95 };
  // Substring match: prefer the longest matching key (more specific).
  let best: { code: CategoryCode; keyLen: number } | null = null;
  for (const [key, code] of Object.entries(VENDOR_TABLE)) {
    if (v.includes(key)) {
      if (best === null || key.length > best.keyLen) {
        best = { code, keyLen: key.length };
      }
    }
  }
  return best === null ? null : { code: best.code, score: 0.75 };
}

/**
 * Score every category against the receipt's item names. For each
 * category, count how many item names contain ANY of the category's
 * keywords (substring-match, lowercased). The dominant category is
 * the one with the highest match count; the score scales with the
 * fraction of items it covers.
 *
 * Returns null when:
 *   - no items
 *   - no category gets any keyword hit
 *   - the top category doesn't beat the runner-up by ≥1 (a tie means
 *     we can't pick confidently)
 */
function matchItemKeywords(
  itemNames: readonly string[],
): { code: CategoryCode; score: number } | null {
  if (itemNames.length === 0) return null;
  const lowerItems = itemNames.map((n) => n.toLowerCase());
  const counts = new Map<CategoryCode, number>();
  for (const [code, keywords] of Object.entries(ITEM_KEYWORDS) as [
    CategoryCode,
    readonly string[],
  ][]) {
    if (keywords.length === 0) continue;
    let hits = 0;
    for (const item of lowerItems) {
      if (keywords.some((k) => item.includes(k))) hits += 1;
    }
    if (hits > 0) counts.set(code, hits);
  }
  if (counts.size === 0) return null;
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  const [topCode, topCount] = ranked[0]!;
  const runnerUp = ranked[1]?.[1] ?? 0;
  if (topCount === runnerUp) return null;

  // Score: at minimum 0.5, climbing to 0.85 when every item agrees on
  // the same category.
  const fraction = topCount / lowerItems.length;
  const score = Math.min(0.85, 0.5 + 0.35 * fraction);
  return { code: topCode, score };
}

export function inferCategory(receipt: Receipt): CategoryInference | null {
  // Receipt has no items + no vendor → nothing to go on.
  // 1. User memory takes precedence (user-confirmed pairings always win).
  const memory = getVendorCategory(receipt.vendor.value);
  if (memory !== undefined) {
    return { category: memory, score: 1.0, source: 'memory' };
  }
  // 2. Vendor table.
  const vendorHit = matchVendorTable(receipt.vendor.value);
  if (vendorHit !== null && vendorHit.score >= APPLY_THRESHOLD) {
    return { category: vendorHit.code, score: vendorHit.score, source: 'vendor' };
  }
  // 3. Item keywords.
  const itemHit = matchItemKeywords(receipt.items.map((it) => it.name.value));
  if (itemHit !== null && itemHit.score >= APPLY_THRESHOLD) {
    return { category: itemHit.code, score: itemHit.score, source: 'items' };
  }
  return null;
}
